import { useState, useEffect } from 'react'
import {
  AlertTriangle,
  Settings,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Sparkles,
  Eye,
  EyeOff,
  Info,
  Shield,
  RefreshCw,
} from 'lucide-react'
import {
  getPriorityConfig,
  updatePriorityConfig,
  bulkReclassifyPriority,
  getBulkPriorityProgress,
  type PriorityConfig,
  type BulkReclassifyResult,
  type BulkReclassifyProgress,
} from '@/services/api'
import { useRole } from '@/hooks/useRole'
import ConfirmModal from '@/components/ConfirmModal'
import {
  AI_PROVIDERS,
  PROVIDER_STYLES,
  REASONING_STYLES,
  getProviderFromModel,
  getProviderInfo,
  getReasoningLevelsForProvider,
  getDefaultReasoningForProvider,
} from '@/config/models'

const DEFAULT_PRIORITY_PROMPT = `You classify customer support email priority. Analyze the email and return ONLY one word.

Rules:
- urgent: Legal threats, GDPR/data requests, payment fraud, account security breach, system outage
- high: Angry/frustrated customer, missing order >7 days, wrong item received, double charge, damaged goods
- normal: Standard questions, order status, shipping inquiries, returns, product questions
- low: General feedback, feature requests, compliments, newsletter inquiries

Output ONLY one word: low, normal, high, or urgent.`

// Module-level cache
let _cachedPriorityConfig: PriorityConfig | null = null

export default function AIPriorityPage() {
  const [config, setConfig] = useState<PriorityConfig>(_cachedPriorityConfig || {
    enabled: false,
    model: 'gpt-5-mini',
    reasoning_effort: 'low',
    system_prompt: undefined,
  })
  const [loading, setLoading] = useState(!_cachedPriorityConfig)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<PriorityConfig>({ ...config })
  const [showPromptPreview, setShowPromptPreview] = useState(false)
  const [defaultPrompt, setDefaultPrompt] = useState(DEFAULT_PRIORITY_PROMPT)

  // Bulk re-classify state (super admin only)
  const { isSuperAdmin } = useRole()
  const [bulkSinceDate, setBulkSinceDate] = useState('')
  const [bulkForce, setBulkForce] = useState(false)
  const [bulkRunning, setBulkRunning] = useState(false)
  const [showBulkConfirm, setShowBulkConfirm] = useState(false)
  const [bulkProgress, setBulkProgress] = useState<BulkReclassifyProgress | null>(null)
  const [bulkResults, setBulkResults] = useState<BulkReclassifyResult | null>(null)

  const handleBulkReclassifyPriority = async () => {
    setShowBulkConfirm(false)
    setBulkRunning(true)
    setBulkResults(null)
    setBulkProgress(null)
    try {
      await bulkReclassifyPriority({
        since_date: bulkSinceDate || undefined,
        force: bulkForce,
      })
      // Polling will handle the rest via useEffect below
    } catch (err: any) {
      setBulkResults({
        total_found: 0,
        reclassified: 0,
        skipped: 0,
        errors: 1,
        error_details: [err.message || 'Unknown error'],
      })
      setBulkRunning(false)
    }
  }

  // Poll bulk priority reclassify progress
  useEffect(() => {
    if (!bulkRunning) return

    const pollProgress = async () => {
      try {
        const data = await getBulkPriorityProgress()
        setBulkProgress(data)

        if (data.status === 'completed' || data.status === 'error') {
          setBulkRunning(false)
          setBulkResults({
            total_found: data.total_found,
            reclassified: data.reclassified,
            skipped: data.skipped,
            errors: data.errors,
            error_details: data.error_details || [],
          })
        }
      } catch (err) {
        console.error('Error polling bulk priority progress:', err)
      }
    }

    pollProgress()
    const interval = setInterval(pollProgress, 2000)
    return () => clearInterval(interval)
  }, [bulkRunning])

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      if (!_cachedPriorityConfig) setLoading(true)
      setError(null)
      const data = await getPriorityConfig()
      if (data.default_prompt) {
        setDefaultPrompt(data.default_prompt)
      }
      const configData: PriorityConfig = {
        enabled: data.enabled,
        model: data.model,
        reasoning_effort: data.reasoning_effort,
        system_prompt: data.system_prompt,
      }
      setConfig(configData)
      setFormData(configData)
      _cachedPriorityConfig = configData
    } catch (err: any) {
      setError(err.message || 'Failed to load priority configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      await updatePriorityConfig(formData)
      setConfig(formData)
      setIsEditing(false)
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({ ...config })
    setIsEditing(false)
    setError(null)
  }

  if (loading && !_cachedPriorityConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <AlertTriangle className="w-8 h-8 mr-3 text-orange-500" />
            AI Priority Classifier
          </h1>
          <p className="text-gray-600 mt-2">
            Automatically classify ticket priority based on email content and urgency
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-600">{error}</span>
        </div>
      )}

      {/* Configuration Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Settings className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Classifier Configuration</h2>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Configure</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-400 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Enabled Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${formData.enabled ? 'bg-orange-100' : 'bg-gray-200'}`}>
                <AlertTriangle className={`w-5 h-5 ${formData.enabled ? 'text-orange-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-900">Priority Classifier</p>
                <p className="text-sm text-gray-500">
                  {formData.enabled
                    ? 'Tickets are automatically prioritized when emails are classified'
                    : 'Manual priority only — all tickets default to Normal'}
                </p>
                {formData.enabled && (
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-600">
                    <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">Low</span>
                    <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">Normal</span>
                    <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">High</span>
                    <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">Urgent</span>
                  </div>
                )}
              </div>
            </div>
            {isEditing ? (
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            ) : (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                formData.enabled ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {formData.enabled ? 'Auto' : 'Manual'}
              </span>
            )}
          </div>

          {/* AI Provider Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              AI Provider
            </label>
            <div className="grid grid-cols-4 gap-2">
              {AI_PROVIDERS.map((provider) => {
                const styles = PROVIDER_STYLES[provider.id]
                const isSelected = getProviderFromModel(formData.model) === provider.id
                return (
                  <button
                    key={provider.id}
                    type="button"
                    disabled={!isEditing}
                    onClick={() => {
                      if (isEditing) {
                        const defaultModel = provider.models.find(m => m.recommended)?.id || provider.models[0].id
                        const newReasoning = getDefaultReasoningForProvider(provider.id, formData.reasoning_effort)
                        setFormData({ ...formData, model: defaultModel, reasoning_effort: newReasoning })
                      }
                    }}
                    className={`relative px-3 py-2.5 rounded-lg border-2 text-center transition-all ${
                      isSelected
                        ? styles.pillActive + ' ' + styles.pillActiveText
                        : styles.pill + ' ' + styles.pillText
                    } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${isSelected ? styles.dot : 'bg-gray-300'}`} />
                      <span className="text-sm">{provider.name}</span>
                    </div>
                    {!provider.available && (
                      <span className="block text-[10px] mt-0.5 opacity-60">Coming Soon</span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Model Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Model
            </label>
            {(() => {
              const currentProvider = getProviderInfo(getProviderFromModel(formData.model))
              const styles = PROVIDER_STYLES[currentProvider?.id || 'openai']
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {currentProvider?.models.map((model) => {
                    const isSelected = formData.model === model.id
                    return (
                      <button
                        key={model.id}
                        type="button"
                        disabled={!isEditing}
                        onClick={() => {
                          if (isEditing) {
                            setFormData({ ...formData, model: model.id })
                          }
                        }}
                        className={`relative px-3 py-3 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? styles.ring + ' bg-gray-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                              {model.name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{model.description}</p>
                          </div>
                          {model.recommended && (
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${styles.badge}`}>
                              Top
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <div className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${styles.dot}`} />
                        )}
                      </button>
                    )
                  })}
                </div>
              )
            })()}
            {!getProviderInfo(getProviderFromModel(formData.model))?.available && (
              <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                This provider is not yet available. Selection will be saved for when it becomes active.
              </p>
            )}
          </div>

          {/* Reasoning Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Reasoning Level
            </label>
            {(() => {
              const levels = getReasoningLevelsForProvider(getProviderFromModel(formData.model))
              return (
                <div className={`grid gap-2 ${levels.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {levels.map((level) => {
                    const isSelected = formData.reasoning_effort === level.id
                    const styles = REASONING_STYLES[level.id]
                    return (
                      <button
                        key={level.id}
                        type="button"
                        disabled={!isEditing}
                        onClick={() => {
                          if (isEditing) {
                            setFormData({ ...formData, reasoning_effort: level.id })
                          }
                        }}
                        className={`px-3 py-3 rounded-lg border-2 text-center transition-all ${
                          isSelected
                            ? styles.cardActive + ' ' + styles.textActive
                            : styles.card + ' ' + styles.text
                        } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                      >
                        <p className="text-sm font-semibold">{level.name}</p>
                        <p className="text-[11px] mt-0.5 opacity-75">{level.description}</p>
                      </button>
                    )
                  })}
                </div>
              )
            })()}
            <p className="mt-2 text-xs text-gray-500">
              Higher reasoning provides better accuracy but increases response time and cost.
            </p>
          </div>

          {/* Custom System Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Classification Prompt
              </label>
              <button
                onClick={() => setShowPromptPreview(!showPromptPreview)}
                className="text-xs text-orange-600 hover:text-orange-700 flex items-center space-x-1"
              >
                {showPromptPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                <span>{showPromptPreview ? 'Hide' : 'Show'} {formData.system_prompt ? 'Current' : 'Default'} Prompt</span>
              </button>
            </div>

            {/* Status indicator */}
            <div className={`mb-3 p-3 rounded-lg border ${
              formData.system_prompt
                ? 'bg-purple-50 border-purple-200'
                : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-start space-x-2">
                {formData.system_prompt ? (
                  <>
                    <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-purple-900">Using Custom Prompt</p>
                      <p className="text-xs text-purple-700 mt-0.5">
                        Your custom rules define how priorities are assigned.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-orange-900">Using Default Prompt</p>
                      <p className="text-xs text-orange-700 mt-0.5">
                        The classifier uses built-in rules optimized for general customer support.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {showPromptPreview && (
              <div className="mb-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-900">
                    {formData.system_prompt ? 'Current Prompt (Custom)' : 'Default Prompt (Active)'}
                  </p>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    formData.system_prompt
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {formData.system_prompt ? 'Custom' : 'Default'}
                  </span>
                </div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border border-gray-200 max-h-64 overflow-y-auto">
                  {formData.system_prompt || defaultPrompt}
                </pre>
              </div>
            )}

            <textarea
              value={formData.system_prompt || ''}
              onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value || undefined })}
              disabled={!isEditing}
              placeholder="Leave empty to use the default prompt, or enter your own custom priority classification rules..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500"
              rows={8}
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional: Override the default prompt with your own priority rules. The prompt must instruct the model to output exactly one of: low, normal, high, or urgent.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Card */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100 p-6">
        <div className="flex items-start space-x-4">
          <div className="p-3 bg-orange-100 rounded-lg">
            <Info className="w-6 h-6 text-orange-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-2">How AI Priority Classifier Works</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                <strong>1.</strong> When the AI Router classifies an email to an agent, the priority classifier runs automatically.
              </p>
              <p>
                <strong>2.</strong> The LLM analyzes the email content and assigns a priority level.
              </p>
              <p>
                <strong>3.</strong> The ticket is updated with the classified priority (Low, Normal, High, or Urgent).
              </p>
              <p>
                <strong>4.</strong> Your team can focus on urgent issues first without manually triaging every email.
              </p>
            </div>
            <div className="mt-4 p-3 bg-white rounded-lg border border-orange-100">
              <p className="text-xs font-medium text-gray-700 mb-2">Priority Levels</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="text-xs text-gray-600"><strong>Low</strong> — Feedback, feature requests</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  <span className="text-xs text-gray-600"><strong>Normal</strong> — Standard inquiries</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  <span className="text-xs text-gray-600"><strong>High</strong> — Frustrated customers, issues</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  <span className="text-xs text-gray-600"><strong>Urgent</strong> — Legal, security, outages</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {!isEditing && !error && !loading && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-600">Priority classifier configuration is saved and active</span>
        </div>
      )}

      {/* Bulk Priority Re-classification (Super Admin Only) */}
      {isSuperAdmin && (
        <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Shield className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Bulk Priority Re-classification</h3>
              <p className="text-sm text-gray-500">Super Admin — Re-run AI Priority on existing classified threads</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Since Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Since Date (optional)</label>
              <input
                type="date"
                value={bulkSinceDate}
                onChange={(e) => setBulkSinceDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                disabled={bulkRunning}
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to process all threads</p>
            </div>

            {/* Options */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkForce}
                  onChange={(e) => setBulkForce(e.target.checked)}
                  disabled={bulkRunning}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Force re-classify (overwrite existing priority)</span>
              </label>
            </div>

            {/* Run Button */}
            <button
              onClick={() => setShowBulkConfirm(true)}
              disabled={bulkRunning}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${bulkRunning ? 'animate-spin' : ''}`} />
              {bulkRunning ? 'Processing...' : 'Run Bulk Priority Re-classify'}
            </button>

            {/* Progress Bar */}
            {bulkRunning && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-orange-700">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{bulkProgress?.message || 'Starting bulk priority reclassify...'}</span>
                  </div>
                  {bulkProgress && bulkProgress.total_found > 0 && (
                    <span className="font-medium">
                      {bulkProgress.reclassified + bulkProgress.skipped + bulkProgress.errors}
                      {' / '}
                      {bulkProgress.total_found}
                    </span>
                  )}
                </div>
                <div className="w-full bg-orange-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-orange-500 rounded-full transition-all duration-300"
                    style={{
                      width: bulkProgress && bulkProgress.total_found > 0
                        ? `${((bulkProgress.reclassified + bulkProgress.skipped + bulkProgress.errors) / bulkProgress.total_found) * 100}%`
                        : '0%'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Results */}
            {bulkResults && !bulkRunning && (
              <div className={`p-4 rounded-lg border ${bulkResults.errors > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Found:</span>
                    <span className="ml-1 font-semibold">{bulkResults.total_found}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Re-classified:</span>
                    <span className="ml-1 font-semibold text-green-700">{bulkResults.reclassified}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Skipped:</span>
                    <span className="ml-1 font-semibold text-gray-700">{bulkResults.skipped}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Errors:</span>
                    <span className="ml-1 font-semibold text-red-700">{bulkResults.errors}</span>
                  </div>
                </div>
                {bulkResults.error_details.length > 0 && (
                  <div className="mt-3 text-xs text-red-600 space-y-1">
                    {bulkResults.error_details.map((e, i) => (
                      <div key={i}>{e}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Priority Re-classify Confirmation Modal */}
      <ConfirmModal
        open={showBulkConfirm}
        onCancel={() => setShowBulkConfirm(false)}
        onConfirm={handleBulkReclassifyPriority}
        title="Bulk Priority Re-classification"
        icon={
          <div className="p-2 bg-orange-100 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
        }
        description={
          <div className="space-y-3">
            <p>
              This will re-classify priority for <strong>{bulkForce ? 'all' : 'only threads without priority'}</strong> classified threads
              {bulkSinceDate ? <> since <strong>{bulkSinceDate}</strong></> : <> <span className="text-gray-500">(all time)</span></>}.
            </p>
            <div className="p-3 bg-orange-50 border border-orange-100 rounded-lg text-xs text-orange-800">
              This operation may take a while and will consume AI API credits. Only priority levels will be updated — agent assignments will not change.
            </div>
          </div>
        }
        confirmLabel="Run Priority Re-classification"
        confirmColor="bg-orange-600 hover:bg-orange-700 focus:ring-orange-500"
        loading={bulkRunning}
      />
    </div>
  )
}
